import { BaseModel } from 'projects/core/src/lib/models/base.model';
import { BehavioranchorModel } from 'projects/core/src/lib/models/behavioranchor.model';
import { ElementModel } from './element.model';
import * as moment from 'moment';
import { AcModel } from './ac.model';
import { AcStatusTimingsType, RatingstartType, SlotStatus, TimerType } from './acstatus/ac-status.type';
import { BehaviorSubject } from 'rxjs';
import { ExerciseModel } from './exercise.model';
import { ExerciseContainer } from '../partials/agenda/agenda/agenda.component';
import { GroupModel } from './group.model';
import { SINGLE_GROUP_UUID } from './constants';

export enum SlotType {
  Exercisepart = 'exercisepart',
  Org = 'org',
  Feedback = 'feedback',
  Onlinetest = 'onlinetest',
  Conference = 'conference',
  Break = 'break'
}


export class SlotModel extends BaseModel {

  // columns
  id: number;
  name: string;
  exercisename: string;
  type: SlotType;
  start_time: number; // tslint:disable-line: variable-name
  duration: number; // minutes
  for_candidate: boolean; // tslint:disable-line: variable-name
  for_observer: boolean; // tslint:disable-line: variable-name
  rating_start: RatingstartType; // tslint:disable-line: variable-name
  rating_duration: number; // tslint:disable-line: variable-name

  exercise_id: number; // tslint:disable-line: variable-name
  exercisepart_id: number; // tslint:disable-line: variable-name
  container_offset: number = 0; // tslint:disable-line: variable-name
  container: ExerciseContainer = null;

  // client timing
  private timings: { [key: string]: AcStatusTimingsType } = {};
  private timingsSubject: BehaviorSubject<{ [key: string]: AcStatusTimingsType }> = new BehaviorSubject(this.timings);
  public timings$ = this.timingsSubject.asObservable();

  // client attributes
  ratingStatus: string = null;
  baseDuration: number;
  baseRatingDuration: number;
  calculatedStartTime = 0;

  // relations
  exercise: ExerciseModel;
  elements: Array<ElementModel> = [];
  behavioranchors: Array<BehavioranchorModel> = [];
  ac: AcModel = null;

  // client
  public messageBeforeFinish: string | null = null;

  deserialize(input: any): this {
    Object.assign(this, input);
    // behavioranchors
    let behavioranchors = null;
    if (this.behavioranchors !== null && this.behavioranchors !== undefined) {
      behavioranchors = [];
      this.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
        behavioranchors.push(new BehavioranchorModel().deserialize(behavioranchor));
      });
    }
    this.behavioranchors = behavioranchors;
    // elements
    let elements = null;
    if (this.elements !== null && this.elements !== undefined) {
      elements = [];
      this.elements.forEach((element: ElementModel) => {
        elements.push(new ElementModel().deserialize(element));
      });
    }
    this.elements = elements;
    // save duration from server
    this.baseDuration = 0 + this.duration;
    this.baseRatingDuration = 0 + this.rating_duration;
    return this;
  }

  public resetTimings(): void {
    this.timings = {};
    this.timingsSubject.next(this.timings);
  }

  public setTiming(hash: string, timing: AcStatusTimingsType): void {
    this.timings[hash] = timing;
    this.timingsSubject.next(this.timings);
  }

  private getTiming(hash: string): AcStatusTimingsType | null {
    return this.timings[hash] || null;
  }

  private getTimingAsCopy(hash: string): AcStatusTimingsType | null {
    return {...this.timings[hash]};
  }

  public getTime(now: moment.Moment, hash: string, mode: TimerType): number | null {
    if (this.timings[hash] && this.timings[hash][mode]) {
      const timing = this.timings[hash][mode];
      let elapsed = timing.elapsedSeconds || 0;
      if (timing.startedAt) {
        const started = moment(timing.startedAt);
        if (timing.pausedAt) {
          const paused = moment(timing.pausedAt);
          elapsed += paused.diff(started, 'seconds');
        } else {
          elapsed += now.diff(started, 'seconds');

        }
        const duration = (mode === TimerType.Rating) ? this.rating_duration : this.duration ;
        return Math.max(0, (duration * 60 ) - elapsed);
      }
    }
    return null;
  }

  public startSlot(hash: string, mode: TimerType = TimerType.Exercise, force = false): AcStatusTimingsType {
    let timing = null;
    if (this.isStartable(hash, mode) || force === true) {
      timing = this.getTimingAsCopy(hash);
      if (mode === TimerType.Rating) {
        timing.rating = {
          status: SlotStatus.Running,
          startedAt: moment().toISOString(),
          pausedAt: null,
          finishedAt: null,
          elapsedSeconds: 0
        };
        if (this.rating_start === RatingstartType.End) {
          timing = {...timing, ...this.finishSlot(hash, TimerType.Exercise)};
        }
      } else {
        timing.exercise = {
          status: SlotStatus.Running,
          startedAt: moment().toISOString(),
          pausedAt: null,
          finishedAt: null,
          elapsedSeconds: 0
        };
        if (this.rating_start === RatingstartType.Start) {
          timing = {...timing, ...this.startSlot(hash, TimerType.Rating)};
        }
      }
    }
    return timing;
  }

  public pauseSlot(hash: string, mode: TimerType = TimerType.Exercise): AcStatusTimingsType {
    let timing = null;
    if (this.isPauseable(hash, mode)) {
      timing = this.getTimingAsCopy(hash);
      if (timing[mode]) {
        timing[mode].status = SlotStatus.Paused;
        timing[mode].pausedAt = moment().toISOString();
      }
    }
    return timing;
  }

  public resumeSlot(hash: string, mode: TimerType = TimerType.Exercise): AcStatusTimingsType {
    let timing = null;
    if (this.isResumeable(hash, mode)) {
      timing = this.getTimingAsCopy(hash);
      if (timing[mode]) {
        timing[mode].status = SlotStatus.Running;
        timing[mode].elapsedSeconds += moment(timing[mode].pausedAt).diff(moment(timing[mode].startedAt), 'seconds');
        timing[mode].startedAt = moment().toISOString();
        timing[mode].pausedAt = null;
      }
    }
    return timing;
  }

  public finishSlot(hash: string, mode: TimerType = TimerType.Exercise): AcStatusTimingsType {
    let timing = null;
    if (this.isFinishable(hash, mode)) {
      timing = this.getTimingAsCopy(hash);
      if (timing[mode]) {
        timing[mode].status = SlotStatus.Finished;
        timing[mode].finishedAt = moment().toISOString();
      }
      if (mode === TimerType.Exercise && this.rating_start === RatingstartType.End) {
        timing = {...timing, ...this.startSlot(hash, TimerType.Rating, true)};
      }
    }
    return timing;
  }

  // can do's?

  public isStartable(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    if (!this.isStopped(hash, mode)) {
      return false;
    }
    // conference
    if (hash === 'conference') {
      let startable = this.ac?.isRunning() && this.isStopped(hash, mode);
      for (const observer of this.ac?.observers) {
        if (observer.isBusy()) {
          startable = false;
          break;
        }
      }
      return startable;
    }
    // rating
    if (mode === TimerType.Rating && !this.isRateable()) {
      return false;
    }
    // check busy flags of users
    if (this.ac?.isSingle()) {
      if (mode === TimerType.Rating) {
        return !this.ac.observers[0].busy;
      } else {
        return (this.for_candidate !== true || !this.ac.candidates[0].busy) &&
          (this.for_observer !== true || !this.ac.observers[0].busy);
      }
    } else {
      let startable = true;
      if (hash.includes('-')) {
        // group
        const group = this.exercise?.groups?.find(actual => actual.hash === hash) || null;
        if (group !== null) {
          // check candidates
          for (const candidate of group.candidates) {
            if (this.for_candidate === true && candidate.isBusy()) {
              startable = false;
              break;
            }
          }
          // check observers
          if (startable === true || !this.for_candidate) {
            for (const observer of group.observers) {
              if (this.for_observer === true && observer.isBusy()) {
                startable = false;
                break;
              }
            }
          }
        }
      } else {
        // check candidate
        const candidate = this.ac?.candidates.find(actual => hash === SINGLE_GROUP_UUID || hash === actual.hashed_id) || null;
        if (candidate !== null) {
          if (mode === TimerType.Exercise && this.for_candidate === true && candidate.isBusy()) {
            return false;
          }
          // check observers
          const group = this.exercise?.groups?.find((group: GroupModel) => {
            return group.candidates.find(actual => actual.hashed_id === candidate.hashed_id) !== undefined;
          }) || null;
          if (group !== null) {
            for (const observer of group.observers) {
              if (this.for_observer === true && observer.isBusy()) {
                startable = false;
                break;
              }
            }
          }
        }
      }
      return startable;
    }
  }

  public isPauseable(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    return this.isRunning(hash, mode);
  }

  public isResumeable(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    return this.isPaused(hash, mode);
  }

  public isFinishable(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    return this.isRunning(hash, mode) || this.isPaused(hash, mode);
  }

  public isResetable(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    return this.ac?.isRunning() && this.isFinished(hash, mode);
  }

  // status

  public isStopped(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    const timing = this.getTimingAsCopy(hash);
    return (mode === TimerType.Rating) ? !timing.rating?.status : !timing.exercise?.status;
  }

  public isRunning(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    const timing = this.getTimingAsCopy(hash);
    return timing !== null && timing[mode]?.status === SlotStatus.Running;
  }

  public isPaused(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    const timing = this.getTimingAsCopy(hash);
    return timing !== null && timing[mode]?.status === SlotStatus.Paused;
  }

  public isFinished(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    const timing = this.getTimingAsCopy(hash);
    return timing !== null && timing[mode]?.status === SlotStatus.Finished;
  }

  public isRunningOrPaused(hash: string, mode: TimerType = TimerType.Exercise): boolean {
    return this.isRunning(hash, mode) || this.isPaused(hash, mode);
  }

  public isRatingActive(hash: string): boolean {
    const timing = this.getTimingAsCopy(hash);
    return this.isRunning(hash, TimerType.Rating);
  }

  public isRateable(): boolean {
    return this.type === SlotType.Exercisepart && this.rating_start && this.rating_start !== 'none';
  }

}
